import { useEffect } from "react"

import { useTranslation } from "react-i18next"

import type { TenderParticipant } from "@/api/tender-api"

import { Crown, Users } from "@/components/icons"

import { PersonPicker, type PickedPerson } from "./person-picker"
import { useUpdateTenderCase } from "../hooks/use-tender-case"
import {
  useAddTenderParticipant,
  useRemoveTenderParticipant,
  useTenderHandoffConfig,
  useTenderParticipants,
} from "../hooks/use-tender-cases"

/**
 * Ansvarig + deltagare, as edited from Setup after the case exists.
 *
 * Both save on pick rather than behind a save button: a directory pick is
 * already a deliberate act, and the surrounding text fields' draft/save pattern
 * exists for typing, not for selection.
 *
 * Gated on `canManage`, not on edit rights. Every write here is a
 * participant-management act — naming the Ansvarig records an address that
 * hand-over later pre-fills and mails — and the endpoints behind both pickers
 * require owner or admin. Enabling them for editors only produced silent 403s.
 *
 * Naming an Ansvarig records intent; it does NOT grant them access. Add them as
 * a deltagare as well if they should see the case before hand-over.
 */
export function CasePeopleFields({
  caseId,
  responsibleUserOid,
  responsibleDisplayName,
  responsibleEmail,
  ownerUserId,
  canManage = false,
}: {
  caseId: string
  responsibleUserOid?: string | null
  responsibleDisplayName?: string
  responsibleEmail?: string
  ownerUserId?: string
  canManage?: boolean
}) {
  const { t } = useTranslation()
  const { data: handoffConfig } = useTenderHandoffConfig()
  const { data: participantsData } = useTenderParticipants(caseId)
  const updateCase = useUpdateTenderCase(caseId)
  const addParticipant = useAddTenderParticipant()
  const removeParticipant = useRemoveTenderParticipant()

  const busy = updateCase.isPending || addParticipant.isPending || removeParticipant.isPending
  const failed = updateCase.isError || addParticipant.isError || removeParticipant.isError

  // react-query keeps isError set until that same mutation runs again, and these
  // three are independent. Without clearing, a failed deltagare add would keep
  // the message up through a later successful Ansvarig save, and would follow
  // the component onto the next case while Setup stays mounted.
  const clearStaleErrors = () => {
    updateCase.reset()
    addParticipant.reset()
    removeParticipant.reset()
  }
  // Deliberately keyed on caseId alone: this is about a case switch, not about
  // the mutation objects, which change identity on every render.
  useEffect(() => {
    clearStaleErrors()
  }, [caseId])

  const responsible: PickedPerson[] = responsibleUserOid
    ? [
        {
          oid: responsibleUserOid,
          displayName: responsibleDisplayName || responsibleUserOid,
          email: responsibleEmail,
        },
      ]
    : []

  // The owner is not a "deltagare" — they already hold the case. The Ansvarig is
  // excluded too: they have their own field above, and listing a person who is
  // both would render them twice, with two different remove buttons.
  const participants: PickedPerson[] = (participantsData?.participants ?? [])
    .filter(
      (p: TenderParticipant) =>
        p.type === "user" && p.oid !== ownerUserId && p.oid !== responsibleUserOid
    )
    .map((p: TenderParticipant) => ({ oid: p.oid, displayName: p.displayName }))

  const handleResponsibleChange = (next: PickedPerson[]) => {
    clearStaleErrors()
    if (next.length === 0) {
      updateCase.mutate({ caseId, responsible: null })
      return
    }
    const picked = next[0]
    updateCase.mutate({
      caseId,
      responsible: { oid: picked.oid, displayName: picked.displayName, email: picked.email },
    })
  }

  const handleParticipantsChange = (next: PickedPerson[]) => {
    clearStaleErrors()
    const added = next.find((p) => !participants.some((existing) => existing.oid === p.oid))
    if (added) {
      addParticipant.mutate({
        caseId,
        participant: {
          oid: added.oid,
          displayName: added.displayName,
          type: "user",
          role: "editor",
        },
      })
      return
    }
    const removed = participants.find((p) => !next.some((kept) => kept.oid === p.oid))
    if (removed) removeParticipant.mutate({ caseId, participantOid: removed.oid })
  }

  return (
    <div className="space-y-4">
      <PersonPicker
        dimmedOids={participants.map((p) => p.oid)}
        disabled={!canManage || busy}
        hint={t("tender.people.responsibleHint")}
        icon={<Crown className="text-muted-foreground size-4 shrink-0" />}
        label={t("tender.people.responsibleLabel")}
        placeholder={t("tender.people.responsiblePlaceholder")}
        scopeGroup={handoffConfig?.userGroupId ?? undefined}
        selected={responsible}
        onChange={handleResponsibleChange}
      />
      <PersonPicker
        multiple
        dimmedOids={[...responsible.map((p) => p.oid), ...(ownerUserId ? [ownerUserId] : [])]}
        disabled={!canManage || busy}
        hint={t("tender.people.participantsHint")}
        icon={<Users className="text-muted-foreground size-4 shrink-0" />}
        label={t("tender.people.participantsLabel")}
        placeholder={t("tender.people.participantsPlaceholder")}
        scopeGroup={handoffConfig?.userGroupId ?? undefined}
        selected={participants}
        onChange={handleParticipantsChange}
      />
      {/* These mutations have no error UI of their own, so a rejected write
          would otherwise just look like nothing happening. */}
      {failed ? <p className="text-destructive text-xs">{t("tender.people.saveFailed")}</p> : null}
    </div>
  )
}
